import {
  BaseGpsData,
  BaseGpsResponseSchema,
  BusGpsData,
  TrainGpsData,
} from "features/CenterContent/shared/types";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { createToast } from "shared/components/Spor";

export const clamp = (min: number, max: number, value: number) =>
  Math.min(Math.max(value, min), max);

const addClosestNeighbor = (
  oldData: BusGpsData[] | TrainGpsData[],
  newData: BaseGpsData[],
) => {
  const closestIndices = oldData.map((it) =>
    it.closestNeighbor
      ? oldData.findIndex(
          (vehicle) => vehicle.closestNeighbor === it.closestNeighbor,
        )
      : null,
  );

  return newData.map((it, index) => {
    const closestIndex = closestIndices[index];
    if (!closestIndex) return it;
    return { ...it, closestNeighbor: newData[closestIndex] };
  });
};

export const busGpsDataToBaseGpsData = (data: BusGpsData[]) => {
  const baseGps = BaseGpsResponseSchema.parse({
    data:
      data.map((it) => ({
        id: [it.workshiftId, it.trainNumber].filter(Boolean).join(" - "),
        phoneNumber: it.phoneNumber,
        timestamp: it.timestamp,
        gps: it.gps,
        closestNeighbor: null,
        path: it.path,
      })) ?? [],
  });
  return addClosestNeighbor(data, baseGps.data);
};

export const trainGpsDataToBaseGpsData = (data: TrainGpsData[]) => {
  const baseGps = BaseGpsResponseSchema.parse({
    data:
      data.map((it) => ({
        id: it.resourceId,
        phoneNumber: null,
        timestamp: it.timestamp,
        gps: it.gps,
        closestNeighbor: null,
        path: null,
      })) ?? [],
  });
  return addClosestNeighbor(data, baseGps.data);
};

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

const fadeOutAndDelete = (child: HTMLElement) => {
  // Set initial style for fading out
  // eslint-disable-next-line no-param-reassign -- purposeful DOM manipulation
  child.style.transition = "opacity 0.5s ease";
  // eslint-disable-next-line no-param-reassign
  child.style.opacity = "0";

  // Remove the element after the fade-out duration
  setTimeout(() => {
    child.parentElement?.removeChild(child);
  }, 500);
};

export const clearAllToasts = () => {
  const toastManager = document.getElementById("toast-group:bottom"); // WARNING! this id may change during spor updates
  if (!toastManager) return;
  const children = Array.from(toastManager.children);
  children.forEach((child) => {
    if (child instanceof HTMLElement) {
      fadeOutAndDelete(child);
    }
  });
};

export const handleCopyToClipboard = async (
  string: string,
  translate?: TFunction<"translation", undefined>,
) => {
  const successText = translate
    ? translate("Kopierte {{string}} til utklippstavlen.", { string })
    : `Kopierte ${string} til utklippstavlen.`;
  const errorText = translate
    ? translate("Kunne ikke kopiere til utklippstavlen. Vennligst prøv igjen.")
    : "Kunne ikke kopiere til utklippstavlen. Vennligst prøv igjen.";
  await navigator.clipboard
    .writeText(string)
    .then(() => {
      createToast({
        text: successText,
        variant: "success",
        duration: 2000,
      });
    })
    .catch(() => {
      createToast({
        text: errorText,
        variant: "critical",
        duration: 3000,
      });
    });
};
